import * as React from 'react'
import {
    useEffect,
    useRef,
    Suspense,
    lazy,
    useMemo,
    useCallback,
} from 'react'

import {
    isOnGraphRoute,
    getBgColorFromUrl,
} from '../stores/bootstrap/urlHelpers'

import useBrowserTheme from '../hooks/useBrowserTheme'
import { useZustand } from '../hooks/useZustand'
import { UserContext } from '../UserContext'
import {
    useContextsStore,
    selectIsPublicView,
} from '../stores/useContextsStore'
import { useGraphStore } from '../stores/useGraphStore'
import { useFilterStore } from '../stores/useFilterStore'
import { useBootstrapStore } from '../stores/useBootstrapStore'
import useGraphSelection from '../hooks/useGraphSelection'
import GraphEmptyState from './GraphEmptyState'
import { DialogsContext } from '../DialogsContext'
import ErrorScreen from '../components/ErrorScreen'
import { isKeywordGraphApp, getVariantName } from '../appVariant'
import { getContextRoute } from '../routes'
import { sendToAnalytics } from '../Analytics'
import { useSnackbar } from '../components/Snackbar'
import { isHideAiActions } from '../components/LeftMenuMigration/legacyBridge'
import useTopicSpotlight from '../components/OnboardingGuide/useTopicSpotlight'
import { useOnboardingStore } from '../stores/useOnboardingStore'
import { getBootNarrationUser } from '../stores/onboardingStorage'
import { STAGES } from '../components/OnboardingGuide/copy'

// Mirrors the prefix→type mapping in ContextDrawerItem.getContextType.
// The graph envelope ships node.label only; meta_/seo_/niche_ prefixes
// imply a different editor route than the default. Other special types
// (MINDMAP/WIKILINKS/etc.) need DB info we don't have here, so the
// default branch in getContextRoute handles them.
const inferContextTypeFromLabel = label => {
    if (!label) return undefined
    if (label.includes('meta_')) return 'META'
    if (label.includes('seo_')) return 'SEO'
    if (label.includes('niche_')) return 'NICHE'
    return undefined
}

const EMBED_SOURCE = isKeywordGraphApp() ? 'keywordgraph-app' : 'infranodus-app'

// Mapping events that happen in the graph to the `analytics_action` events in this app (for backwards compatibility)
const ANALYTICS_ACTION_MAP = {
    'graph_action:open_analytics_panel': 'toggle_analytics',
    'graph_action:minimize_layer_panel': 'toggle_analytics',
    'topics_action:select_topic': 'select_community',
    'topics_action:click_keyword': 'click_keyword_in_topic',
    'topics_action:copy_topics': 'copy_communities',
    'topics_action:copy_topic': 'copy_community',
    'topics_action:copy_gaps': 'copy_gaps',
    'topics_action:copy_latent_topics': 'copy_latent_topics',
    'topics_action:filter_topic': 'filter_community',
    'topics_action:remove_topic': 'delete_community',
    'topics_action:toggle_extra_info': 'show_extended_communities',
    'topics_action:toggle_bigger_window': 'increase_analytics_size',
    'topics_action:reload_gap': 'reload_gap',
    'topics_action:bridge': 'gap_bridge_click',
    'graph_action:open_gap_hint': 'highlight_gap',
    'graph_action:open_advice_popover': 'click_structure_advice',
    'node_action:toggle_table_show': 'stats_analytics',
    'node_action:toggle_bigger_window': 'increase_analytics_size',
    'node_action:expand_related_nodes': 'expand_related_nodes',
    'node_action:copy_related_nodes': 'copy_related_nodes',
    'node_action:copy_influential': 'copy_influential',
    'node_action:copy_concepts': 'copy_all_concepts',
    'node_action:copy_bigrams': 'copy_bigrams',
    'node_action:copy_gateway_nodes': 'copy_gateway',
    'node_action:toggle_hidden_menu': 'toggle_hidden_nodes_menu',
    'node_action:restore_all_hidden': 'restore_all_hidden_nodes',
    'graph_action:reveal_underlying': 'remove_influential_nodes',
}

// Resolves a legacy `analytics_action` action name from a forwarded graph event,
// or null if none applies. Handles the gated rows (chip / section / to) and the
// AI-action derivation. The AI analytics event carries action/source/section/ui/
// from but NOT meta.scope, so scope-only legacy names aren't recoverable here.
const deriveAnalyticsAction = (eventName, properties) => {
    const {
        action,
        chip,
        section,
        to_mode: toMode,
        ui,
        graph_source,
        source,
    } = properties

    // Discourse Diversity panel — distinguish the diversity chip from the
    // modularity/combined (network structure) chip.
    if (eventName === 'graph_action' && action === 'toggle_diversity_info') {
        if (chip === 'diversity') return 'click_diversity_score'
        if (chip === 'modularity' || chip === 'combined') {
            return 'network_structure_analytics'
        }
        return null
    }

    if (
        eventName === 'node_action' &&
        action == 'node_click' &&
        graph_source == 'concepts_menu'
    ) {
        return 'click_node_analytics'
    }

    // Switching to the Trends layer.
    if (eventName === 'change_graph_mode' && toMode === 'trends') {
        return 'trends_analytics'
    }
    if (eventName === 'change_graph_mode' && toMode === 'gaps') {
        return 'analytics_insight'
    }
    if (eventName === 'change_graph_mode' && toMode === 'topics') {
        return 'analytics'
    }
    if (eventName === 'change_graph_mode' && toMode === 'concepts') {
        return 'analytics_relations'
    }

    // AI actions — first match wins. Bounded to action/section/ui (no scope).
    if (eventName === 'ai_action') {
        if (ui === 'connected_minibutton') return 'show_related_to_ai'
        if (action === 'question') return 'show_ai_question'
        if (action === 'develop') {
            return section === 'latent_topics' || section == 'gateways'
                ? 'generate_latent_idea'
                : 'show_ai_idea'
        }
        if (section == 'connected') {
            return 'show_related_to_ai'
        }
        if (section == 'bigrams') {
            return 'bigrams_to_ai'
        }
        if (section == 'concepts') {
            return 'concepts_to_ai'
        }
        if (section == 'main_topics') {
            return 'topics_to_ai'
        }
        if (source == 'gaps_menu') {
            return 'latent_topics_to_ai'
        }

        if (action === 'summarize' || action === 'summarize_topics') {
            return 'graph_summary'
        }

        if (action == 'chat') {
            return 'chat_to_ai'
        }

        return null
    }

    // Simple table-driven rows.
    return ANALYTICS_ACTION_MAP[`${eventName}:${action}`] || null
}

// Mirror a bridged legacy event to the host's PostHog, matching the old EJS
// analytics panel, which captured these directly via the global `posthog` using
// the legacy name as the event name (e.g. posthog.capture('search_graph', {…})).
// The global posthog is the host's project — separate from the graph package's
// own namespaced instance.
const capturePostHog = (name, extra = {}) => {
    if (typeof posthog !== 'undefined') {
        try {
            posthog.capture(name, { source: EMBED_SOURCE, ...extra })
        } catch (e) {}
    }
}

// App variant the graph is running inside ('infranodus' | 'keywordgraph'),
// resolved from window.__VARIANT_CONFIG__ in appVariant.js.
const APP_VARIANT = getVariantName()

// Preset for the graph's "AI:" split button on load. This is only a preset —
// the package gives the user's previously chosen action (localStorage in the
// graph package) higher precedence, so we never write it here. Both 'summary'
// and 'graph_summary' map to "Summarize whole graph"; the KeywordGraph variant
// uses 'summarize_topics' to summarize the topics layer, InfraNodus uses 'summary' (package default).
// See node_modules/@infranodus/infranodus-graph/docs/graph-onload-parameters.md
// for the full value list. Could later be derived from a user preference / route.
const DEFAULT_AI_ACTION = isKeywordGraphApp() ? 'summarize_topics' : 'summary'

const getGraphTheme = (theme, customBackgroundColor) => ({
    backgroundColor:
        customBackgroundColor || (theme === 'dark' ? '#111111' : '#f0ecec'),
    mode: theme,
})

const selectorTheme = state => state.theme

const loadGraphModule = () => {
    // CSS deliberately not awaited — the graph may mount while its
    // stylesheet chunk is still in flight.
    import(/* webpackPrefetch: true */ '@infranodus/infranodus-graph/style.css')
    import('./LazyGraph.css')
    return import(/* webpackPrefetch: true */ '@infranodus/infranodus-graph').then(
        mod => ({
            default: mod.ExtractedGraph,
        })
    )
}

// Kick the multi-MB chunk download as soon as index.js executes on graph
// routes, in parallel with the bootstrap API waterfall — not after mount.
let eagerGraphModulePromise = isOnGraphRoute() ? loadGraphModule() : null
// Drop a failed eager promise so lazy() retries with a fresh import.
if (eagerGraphModulePromise) {
    eagerGraphModulePromise.catch(() => {
        eagerGraphModulePromise = null
    })
}

const LazyExtractedGraph = React.memo(
    lazy(() => eagerGraphModulePromise || loadGraphModule())
)

function LazyGraph() {
    // `?hide_ai_actions=1` — emitted by `generateShareURL` alongside
    // `hide_max=1` (the "hide statements & AI panel" Share-dialog
    // checkbox) so the in-graph AI control row stays hidden on
    // shared/embed loads, not just the host's AI panel. Read inside the
    // component (not at module eval) so SPA navigation between URLs
    // with/without the param is reflected — a module-level constant
    // would freeze to the URL of the *first* page load.
    const hideAiActions = isHideAiActions()

    // Graph selection state - moved here from Home
    const {
        currentNodeSelection,
        highlightedEdges,
        filteredEdges,
        removedEdges,
        selectedTopics: selectedGroups,
        onSelectedNodes,
    } = useGraphSelection()

    const browserTheme = useBrowserTheme(selectorTheme)
    const customBackgroundColor = getBgColorFromUrl()
    const graphTheme = useMemo(
        () => getGraphTheme(browserTheme, customBackgroundColor),
        [browserTheme, customBackgroundColor]
    )
    const {
        flags = {},
        flagsLoading = false,
        username: currentUsername,
    } = React.useContext(UserContext)
    const isPublicViewer = useContextsStore(selectIsPublicView)

    // While the first-entry flow owns the empty state (it renders its own
    // GraphEmptyState with an extra "import data" action — see
    // components/FirstEntry), don't stack a second copy here.
    const { firstEntryDialog } = React.useContext(DialogsContext)

    const showSnackbar = useSnackbar(state => state.showSnackbar)

    const topics = useZustand(state => state.topics)
    const currentGraphMode = useZustand(state => state.currentGraphMode)
    const currentGraphControlsMode = useZustand(
        state => state.currentGraphControlsMode
    )
    const currentContext = useZustand(state => state.currentContext)
    const currentContextType = useZustand(state => state.currentContextType)
    const currentCompareMode = useZustand(state => state.currentCompareMode)
    const statements = useZustand(state => state.statements)
    const insightClusters = useZustand(state => state.insightClusters)
    // Community ids of the AI's default bridge pair, passed to the graph package
    // so it can highlight those two topics on the Topics layer (chip outline +
    // animated ring). Informational only — not a selection.
    const insightClusterIds = useMemo(
        () => (insightClusters || []).map(c => c.id),
        [insightClusters]
    )
    const groupNames = useMemo(
        () =>
            topics.map(({ id, aiName }) => ({
                id: id,
                name: aiName,
            })),
        [topics]
    )
    // Onboarding only. Kept out of `graphProps` so its 1.5s cadence doesn't churn that memo.
    const spotlightTopicIds = useTopicSpotlight(topics)
    // A load that began under the onboarding narration lays out out of sight,
    // so its intro swing is handed to the guide: `orbitTrigger` 0 holds it, and
    // the guide's graphRevealTick fires it when the overlay comes off. Every
    // other load keeps the package's own swing-on-settle.
    const [coveredByNarration] = React.useState(() => {
        const guide = useOnboardingStore.getState()
        return (
            !!getBootNarrationUser() ||
            (guide.status === 'active' && guide.stage === STAGES.BRING)
        )
    })
    const graphRevealTick = useOnboardingStore(state => state.graphRevealTick)

    // Get pinned nodes state from filter store
    const filteredTopics = useFilterStore(state => state.recalculate.topics)
    const stopWords = useFilterStore(state => state.stopWords)

    // Get graph data from the new store
    // Subscribe to transformedGraph which has been mapped to the correct format
    const graphObject = useGraphStore(state => state.graphData)
    const originalGraphObject = useGraphStore(state => state.originalGraphData)
    const isLoadingGraph = useGraphStore(state => state.isLoading)
    const isRecalculatedGraph = useGraphStore(
        state => state.isRecalculatedGraph
    )

    const baseGraphObject = originalGraphObject || graphObject

    const onRemovedNodes = useCallback(
        async removedNodeLabels => {
            const { synthesisType, viewType } = useBootstrapStore.getState()
            const isSearchView = viewType === 'search'
            if (!currentContext && !synthesisType && !isSearchView) {
                console.warn(
                    '[LazyGraph] No context, synthesis, or search view active'
                )
                return
            }

            const newStopWords = [...new Set([...removedNodeLabels])]

            useFilterStore.getState().setStopWords(newStopWords)
        },
        [currentContext]
    )

    // Determine which graph to use as the main graph
    // When recalculated, use the recalculated graph data directly as the main graph
    // graphObject already has the correct structure: { entriesAndGraphOfContext: ... }
    const activeGraph = isRecalculatedGraph ? graphObject : baseGraphObject

    const onAiAction = useCallback(
        (action, meta) => {
            // Per-node Action chip on a context-type node (diamond). React-only
            // signal — see node_modules/@infranodus/infranodus-graph/docs/ai-advice-meta-contract.md §3.1.
            // The destination context's name is the node label; userName comes
            // from the graph payload's top-level field (graph owner, not viewer).
            if (action === 'open_context') {
                // Context-type nodes are labeled `context:<name>` in the graph
                // payload; strip the prefix to get the bare context name used
                // in InfraNodus URLs.
                const rawLabel = meta?.node?.label
                const contextName = rawLabel?.replace(/^context:\s*/, '')
                const contextId = meta?.context?.contextId

                if (!contextName && contextId == null) {
                    console.warn(
                        '[LazyGraph] open_context: missing contextName and contextId, cannot navigate',
                        meta
                    )
                    showSnackbar(
                        'error',
                        "Couldn't open this context — graph payload is missing identifiers."
                    )
                    return
                }

                // Owner falls back to the page's graph owner (URL's first path
                // segment for public viewers, logged-in user otherwise). The
                // graph payload often omits userName in synthesis/recalc dumps,
                // and the menu already navigates using this same value.
                const graphOwnerUsername =
                    meta?.context?.userName ||
                    useBootstrapStore.getState().graphOwnerUsername ||
                    currentUsername

                if (!graphOwnerUsername) {
                    console.warn(
                        '[LazyGraph] open_context: no owner username available, cannot navigate',
                        meta
                    )
                    showSnackbar(
                        'error',
                        "Couldn't open this context — owner is unknown."
                    )
                    return
                }

                const contextType = inferContextTypeFromLabel(contextName)
                const link = getContextRoute(
                    contextName,
                    '',
                    contextType,
                    graphOwnerUsername,
                    isPublicViewer
                )

                // Forward currently-pinned concept nodes as ?keywords= so the
                // destination context loads with the same selection (mirrors the
                // ?stopwords= flow). Strip context-filter pins (the `context: …`
                // entries) — those are synthesis-view source filters and don't
                // apply on the destination page.
                const currentPinned =
                    useFilterStore.getState().pinnedNodes || []
                const keywordPins = currentPinned.filter(
                    n => !n.startsWith('context: ')
                )

                const url = new URL(`${window.location.origin}/${link}`)
                if (keywordPins.length > 0) {
                    url.searchParams.set('keywords', keywordPins.join(' '))
                }

                sendToAnalytics({
                    userId: currentUsername || null,
                    event: 'graph_action',
                    data: {
                        source: 'graph_node_action_chip',
                        action: 'open_context',
                        contextName,
                        contextId,
                        graphOwner: graphOwnerUsername,
                        isPublic: meta?.context?.isPublic,
                        keywords: keywordPins,
                    },
                })

                window.location.href = url.toString()
                return
            }

            // Non-AI "Select" affordance from the graph (insight clusters /
            // pathways / selected topic): mirror the clusters into the filter
            // store so statements filter to them, then reveal the statements
            // panel. Never triggers an AI request.
            if (action === 'select_topics') {
                const filterStore = useFilterStore.getState()
                const incomingTopicIds = new Set(
                    (meta?.topics || []).map(String)
                )
                const loadedTopics = filterStore.availableOptions.topics || []
                const newTopics = loadedTopics.filter(t =>
                    incomingTopicIds.has(String(t.id))
                )
                filterStore.setHighlight({ topics: newTopics })
                // Concept-node selections (gateways, animated-edge endpoints,
                // selected concepts) arrive as meta.nodes → pin them so statements
                // filter to those concepts.
                const newPinned = (meta?.nodes || []).map(n =>
                    String(n).replace(/ /g, '_')
                )
                filterStore.setPinnedNodes(newPinned)
                if (window.myEventEmitter) {
                    window.myEventEmitter.emit('openStatementsPanel')
                }
                return
            }

            // Dual-shape contract: prefer explicit meta from the graph when
            // present (meta.version >= 1) — disambiguates scope without reading
            // cached selection state, and removes the auto-select propagation
            // race on Concepts/Trends layers. See
            // node_modules/@infranodus/infranodus-graph/docs/ai-advice-meta-contract.md.
            if (meta && meta.version >= 1) {
                // Populate filter-store selection from meta BEFORE setForceRequestMode.
                // The graph also broadcasts onSelectedTopics/onSelectedNodes, but
                // those land after a React render commit. ChatWidget opens
                // synchronously on forceRequestMode change and reads filter-store
                // state at that moment — without this, auto-selected trending
                // topics or main concepts would race against the cached state
                // from a previous interaction (e.g. a Gap selection).
                const filterStore = useFilterStore.getState()
                const incomingTopicIds = new Set(
                    (meta.topics || []).map(String)
                )
                const loadedTopics = filterStore.availableOptions.topics || []
                const newTopics = loadedTopics.filter(t =>
                    incomingTopicIds.has(String(t.id))
                )
                filterStore.setHighlight({ topics: newTopics })
                const newPinned = (meta.nodes || []).map(n =>
                    String(n).replace(/ /g, '_')
                )
                filterStore.setPinnedNodes(newPinned)

                let requestModeName
                if (meta.action === 'summarize') {
                    // graph_topics → whole-graph topical summary;
                    // graph_concepts / selected_concepts → "summary" template
                    //   ("Summarise the displayed graph") which scopes around
                    //   pinned concepts naturally;
                    // selected_topics / trending_topics / gap → selected graph
                    //   structure summary.
                    if (
                        meta.scope === 'graph_concepts' ||
                        meta.scope === 'selected_concepts'
                    ) {
                        requestModeName = 'summary'
                    } else if (meta.scope === 'graph_topics') {
                        requestModeName = 'graph summary'
                    } else {
                        if (meta?.topics?.length > 0) {
                            requestModeName = 'selected graph summary'
                        } else {
                            requestModeName = 'summary'
                        }
                    }
                } else {
                    const actionToRequestMode = {
                        question: 'question',
                        // "Develop" digs into the selected/insight clusters:
                        // the dedicated 'develop' request mode answers with the
                        // depth of 'response' but at the short length of 'idea'.
                        develop: 'develop',
                        chat: 'gptchat',
                        context: 'context',
                        context_gap: 'context_gap',
                        // Graph requests a content outline (hubs / spokes
                        // strategy) or article outline (single-article
                        // structure) → corresponding AI chat request mode.
                        content_outline: 'content outline',
                        article_outline: 'article outline',
                        // "Go beyond the discourse" — divergent perspective
                        // outside the existing graph (AI module's transcend
                        // request mode).
                        transcend: 'transcend',
                        // Cross-domain metaphor that recontextualizes the graph
                        // concepts (AI module's metaphor request mode — modelled
                        // on transcend but keeps the concepts).
                        metaphor: 'metaphor',
                        // Names what is completely missing from the structure yet
                        // would still relate to it (AI module's missing request
                        // mode — modelled on transcend but keeps the concepts).
                        missing: 'missing',
                    }
                    requestModeName = actionToRequestMode[meta.action]
                }
                if (!requestModeName) {
                    console.warn('[LazyGraph] Unknown meta action/scope:', meta)
                    return
                }
                const useInsightClusters =
                    meta.action === 'question' ||
                    meta.action === 'develop' ||
                    meta.action === 'context_gap'
                useZustand.getState().setForceRequestMode({
                    name: requestModeName,
                    useInsightClusters,
                    customInstruction: '',
                })
                return
            }

            // Legacy path — graph version pre-meta. Derive scope from cached
            // selection state via highlight.topics.
            const hasSelectedTopics =
                useFilterStore.getState().highlight.topics.length > 0

            const actionToRequestMode = {
                question: 'question',
                // Same mapping as the meta path above: develop → develop.
                develop: 'develop',
                summarize: hasSelectedTopics
                    ? 'selected graph summary'
                    : 'graph summary',
                context: 'context',
                context_gap: 'context_gap',
                // Graph requests a content outline (hubs / spokes strategy)
                // or article outline (single-article structure) → corresponding
                // AI chat request mode.
                content_outline: 'content outline',
                article_outline: 'article outline',
                // "Go beyond the discourse" — divergent perspective outside
                // the existing graph (AI module's transcend request mode).
                transcend: 'transcend',
                // Cross-domain metaphor that recontextualizes the graph concepts
                // (AI module's metaphor request mode — modelled on transcend but
                // keeps the concepts in the prompt).
                metaphor: 'metaphor',
                // Names what is completely missing from the structure yet would
                // still relate to it (AI module's missing request mode — modelled
                // on transcend but keeps the concepts in the prompt).
                missing: 'missing',
                // Opens the AI side panel in free chat mode; selections (topics,
                // pinned concepts, graph) come from useAiChatWidgetData like other modes.
                chat: 'gptchat',
            }

            const requestModeName = actionToRequestMode[action]
            if (!requestModeName) {
                console.warn('[LazyGraph] Unknown AI action:', action)
                return
            }

            const useInsightClusters =
                action === 'question' ||
                action === 'develop' ||
                action === 'context_gap'

            // TODO: Remove this when we have a better way to
            // handle custom instructions
            useZustand.getState().setForceRequestMode({
                name: requestModeName,
                useInsightClusters,
                customInstruction: '',
            })
        },
        [currentUsername, isPublicViewer, showSnackbar]
    )

    const onSelectedTopics = useCallback(selectedTopicIds => {
        const loadedTopics = useFilterStore.getState().availableOptions.topics
        const currentTopics = useFilterStore.getState().highlight.topics || []
        const currentIds = new Set(currentTopics.map(t => String(t.id)))
        const incomingIds = new Set(selectedTopicIds.map(id => String(id)))

        if (
            currentIds.size === incomingIds.size &&
            [...currentIds].every(id => incomingIds.has(id))
        ) {
            return
        }

        const newTopics = loadedTopics.filter(topic =>
            incomingIds.has(String(topic.id))
        )
        useFilterStore.getState().setHighlight({ topics: newTopics })
    }, [])

    const onFilteredTopicsFn = useCallback(topicIdForFiltering => {
        const loadedTopics = useFilterStore.getState().availableOptions.topics
        const currentTopics = useFilterStore.getState().recalculate.topics
        const newTopics = loadedTopics.filter(topic =>
            topicIdForFiltering.includes(topic.id.toString())
        )
        useFilterStore.getState().setRecalculate({
            topics: [...currentTopics, ...newTopics],
        })
    }, [])

    // Mirror the graph package's controls / analytics panel state into
    // Zustand on user-driven toggles. `currentGraphControlsMode` is what
    // `generateShareURL` reads when emitting `&controls=` — without this
    // bridge the share URL would only ever reflect the initial `?controls=`
    // URL param, not whatever the user landed on after interacting with
    // the graph. The package skips the initial mount call, so the host's
    // pre-existing state (seeded from URL or null heuristic) isn't
    // clobbered on load.
    const onControlsToggle = useCallback(mode => {
        useZustand.getState().setCurrentGraphControlsMode(mode)
    }, [])

    // Bridge graph-package interaction events into the host's analytics. The
    // package dispatches an `infranodus:graph-event` window CustomEvent from its
    // central sendEvent; here we handle it in two additive ways:
    //   1. Mirror EVERY event into the host's Mixpanel as a single
    //      `graph_action` event carrying the graph's own family + action and all
    //      of its qualities as metadata. This replicates the legacy sigma bridge,
    //      where each interaction fired `myEventEmitter.emit('onGraphAction',
    //      { action, ... })` and the WorkflowStepper listener recorded it as
    //      `graph_action` (e.g. GraphMenuLogic.ejs zoom, Graph.ejs drag/download).
    //      For per-event legacy flat names (step 2 below) this is the ONLY
    //      Mixpanel write, so Mixpanel never sees names like `drag_graph`.
    //   2. Additionally map the curated subset to their legacy flat names to `analytics_action` (Mixpanel + PostHog), so
    //      pre-existing dashboards keep working.
    useEffect(() => {
        const handleGraphEvent = event => {
            console.log('[InfraNodus] Graph event:', event)
            const detail = event.detail || {}
            const { eventName, properties = {} } = detail
            if (!eventName) return

            // The layer chips live in the package; keep the app's notion of the
            // layer in step so `setCurrentGraphMode` can switch back later.
            if (eventName === 'change_graph_mode' && properties.to_mode) {
                useZustand.getState().setCurrentGraphMode(properties.to_mode)
            }

            // (1) Comprehensive `graph_action` mirror. `source` stays the host
            // embed source (matching the other bridged events); the graph's own
            // `source` (e.g. `ai_menu`, `insight_clusters`) is preserved as
            // `graph_source`. `user` is dropped (identity is set via userId).
            const graphMeta = { ...properties }
            delete graphMeta.user

            const eventMetaData = {
                ...graphMeta,
                ...(graphMeta.source ? { graph_source: graphMeta.source } : {}),
                source: EMBED_SOURCE,
                event_name: eventName,
                action:
                    graphMeta.action == 'hide_nodes' &&
                    graphMeta.source == 'selection_badge'
                        ? 'hide_node'
                        : graphMeta.action || eventName,
            }
            sendToAnalytics({
                userId: currentUsername || null,
                event: 'graph_action',
                data: eventMetaData,
            })

            capturePostHog('graph_action', eventMetaData)

            const analyticsAction = deriveAnalyticsAction(eventName, properties)
            const analyticsEventMetaData = {
                source: EMBED_SOURCE,
                action: analyticsAction,
                ...properties,
            }
            if (analyticsAction) {
                sendToAnalytics({
                    userId: currentUsername || null,
                    event: 'analytics_action',
                    data: analyticsEventMetaData,
                })
                capturePostHog(analyticsAction, analyticsEventMetaData)

                if (analyticsAction && analyticsAction.indexOf('copy_') != -1) {
                    sendToAnalytics({
                        userId: currentUsername || null,
                        event: 'value_action',
                        data: {
                            original_event: 'analytics_action',
                            source: EMBED_SOURCE,
                            action: analyticsAction,
                            ...(properties.panel_window_size
                                ? {
                                      panel_window_size:
                                          properties.panel_window_size,
                                  }
                                : {}),
                        },
                    })
                }
            }
        }

        window.addEventListener('infranodus:graph-event', handleGraphEvent)
        return () =>
            window.removeEventListener(
                'infranodus:graph-event',
                handleGraphEvent
            )
    }, [currentUsername])

    // Emit the legacy `load_graph` event once, the first time the graph becomes
    // available. The package exposes no onReady/onLoad callback, so we key off
    // the host's own store state.
    const loadGraphTrackedRef = useRef(false)
    useEffect(() => {
        if (
            !loadGraphTrackedRef.current &&
            baseGraphObject &&
            !isLoadingGraph
        ) {
            loadGraphTrackedRef.current = true
            sendToAnalytics({
                userId: currentUsername || null,
                event: 'graph_action',
                data: { source: EMBED_SOURCE, action: 'load_graph' },
            })
            capturePostHog('graph_action', {
                source: EMBED_SOURCE,
                action: 'load_graph',
            })
        }
    }, [baseGraphObject, isLoadingGraph, currentUsername])

    const recalculatedGraph = useMemo(
        () =>
            isRecalculatedGraph
                ? {
                      entriesAndGraphOfContext:
                          activeGraph.entriesAndGraphOfContext,
                  }
                : null,
        [isRecalculatedGraph, activeGraph]
    )

    // Consolidate all graph props into a single object
    const graphProps = useMemo(
        () => ({
            // uncomment below if use the recalculated graph prop
            recalculatedGraph,
            graph: baseGraphObject,
            // graph: activeGraph,
            theme: graphTheme,
            selectedNodes: currentNodeSelection,
            removedNodes: stopWords,
            onRemovedNodes,
            selectedGroups,
            groupNames,
            insightClusters: insightClusterIds,
            graphLayer: currentGraphMode,
            graphContextType: currentContextType,
            graphCompareMode: currentCompareMode,
            onSelectedNodes,
            onAiAction,
            onSelectedTopics,
            onFilteredTopics: onFilteredTopicsFn,
            filteredTopics,
            // Conditionally add highlightedEdges if no groups selected and no removed edges
            highlightedEdges:
                selectedGroups.length === 0 &&
                (!removedEdges || removedEdges.length === 0)
                    ? highlightedEdges
                    : null,
            removedEdges,

            // When recalculated graph is active, we don't need removedEdges
            // since the graph is already filtered
            // ...(isRecalculatedGraph && { removedEdges: [] }),
        }),
        [
            recalculatedGraph,
            baseGraphObject,
            graphTheme,
            currentNodeSelection,
            stopWords,
            onRemovedNodes,
            selectedGroups,
            groupNames,
            insightClusterIds,
            currentGraphMode,
            currentContextType,
            currentCompareMode,
            onSelectedNodes,
            onAiAction,
            onSelectedTopics,
            onFilteredTopicsFn,
            filteredTopics,
            highlightedEdges,
            removedEdges,
        ]
    )

    useEffect(() => {
        console.log('[InfraNodus] Updating graph props:', graphProps)
    }, [graphProps])

    // Don't render anything if feature flags are still loading
    if (flagsLoading) {
        return <div className="graph-placeholder">Loading...</div>
    }

    // Only render the 3D graph if the new_3d_graph feature flag is enabled
    if (!flags.new_3d_graph) {
        return (
            <ErrorScreen
                title="Graph view unavailable"
                message="The graph could not be loaded for your account. Please contact support for assistance."
            />
        )
    }

    // If graph is still loading from API, show loading state
    // But keep graph visible during recalculation
    if (isLoadingGraph && !baseGraphObject) {
        return <div className="graph-placeholder">Loading graph data...</div>
    }

    // If no graph object available, show message
    if (!baseGraphObject) {
        return <div className="graph-placeholder">No graph data available</div>
    }

    // empty iff the current view's graph has no nodes (works across
    // context/synthesis/search)
    const graphNodes =
        baseGraphObject?.entriesAndGraphOfContext?.graph?.graphologyGraph?.nodes
    const isEmpty = !isLoadingGraph && (!graphNodes || graphNodes.length === 0)

    return (
        <div className="graph-container">
            {isEmpty && !firstEntryDialog && <GraphEmptyState />}
            <div
                suppressHydrationWarning
                style={{
                    zIndex: 10,
                    position: 'fixed',
                    // The graph canvas sizes itself to the full window, so this
                    // fixed wrapper must sit at the viewport origin. Any non-zero
                    // offset here shifts the whole canvas and leaves a bare strip
                    // on the opposite edges (was right:15px/top:-50px → 15px right
                    // + 50px bottom gaps and an up-left shift of the graph).
                    top: '0px',
                    right: '0px',
                }}
            >
                <Suspense
                    fallback={
                        <div className="graph-placeholder">
                            Loading Graph...
                        </div>
                    }
                >
                    <LazyExtractedGraph
                        {...graphProps}
                        spotlightTopicIds={spotlightTopicIds}
                        embedSource={EMBED_SOURCE}
                        defaultAiAction={DEFAULT_AI_ACTION}
                        appVariant={APP_VARIANT}
                        controls={currentGraphControlsMode}
                        onControlsToggle={onControlsToggle}
                        hideAiActions={hideAiActions}
                        orbit={500}
                        orbitFor={1500}
                        orbitStyle={'pingpong'}
                        orbitDegree={40}
                        orbitTrigger={
                            coveredByNarration ? graphRevealTick : undefined
                        }
                    />
                </Suspense>
            </div>
        </div>
    )
}

// Memo prevents re-renders from parent - internal hooks handle their own updates
export default React.memo(LazyGraph)
